import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, X, CreditCard, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Always fetch plans, even if user is not logged in
    fetchPlans();
    
    // Fetch profile and subscription status only if user is logged in
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setMyProfile(data);
        fetchSubscriptionStatus(data.id);
      } else {
        // Profile doesn't exist - user can still view plans but can't subscribe without profile
        console.log('Researcher profile not found - user can view plans but needs to create profile to subscribe');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load subscription plans",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async (researcherId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/subscription-status/${researcherId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async (planKey) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to subscribe to a plan",
        variant: "destructive"
      });
      navigate('/');
      return;
    }
    
    // Use user.id as researcher_id if no profile exists
    const researcherId = myProfile ? myProfile.id : user.id;
    await createSubscription(planKey, researcherId);
  };
  
  const createSubscription = async (planKey, researcherId) => {

    setProcessing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_key: planKey,
          researcher_id: researcherId,
          return_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription/cancel`
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to PayPal approval URL
        window.location.href = data.approval_url;
      } else {
        const error = await response.json();
        toast({
          title: "Subscription Error",
          description: error.detail || "Failed to create subscription",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to connect to payment system",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setProcessing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/cancel-subscription?researcher_id=${myProfile.id}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Subscription cancelled successfully"
        });
        fetchSubscriptionStatus(myProfile.id);
        fetchProfile();
      } else {
        toast({
          title: "Error",
          description: "Failed to cancel subscription",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  const plansByType = {
    premium: {
      usd: plans.premium_usd,
      sar: plans.premium_sar
    },
    advanced: {
      usd: plans.advanced_usd,
      sar: plans.advanced_sar
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('choosePlan')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('selectPerfectPlan')}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center items-center gap-4 mb-12">
          <span className={`text-lg font-medium ${!isYearly ? 'text-cyan-600' : 'text-gray-600'}`}>
            {t('monthly')}
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className="relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
            style={{ backgroundColor: isYearly ? '#06b6d4' : '#d1d5db' }}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                isYearly ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-lg font-medium ${isYearly ? 'text-cyan-600' : 'text-gray-600'}`}>
            {t('yearly')}
          </span>
          {isYearly && (
            <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
              Save 20%
            </span>
          )}
        </div>

        {/* Current Subscription Status */}
        {currentSubscription && currentSubscription.has_subscription && (
          <Card className="mb-8 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-900">
                    {t('currentPlanLabel')}: {currentSubscription.plan === 'basic' && t('basic')}
                    {currentSubscription.plan === 'premium' && t('premium')}
                    {currentSubscription.plan === 'advanced' && t('advanced')}
                  </h3>
                  <p className="text-sm text-green-700">
                    {t('status')}: {currentSubscription.status} | {currentSubscription.amount} {currentSubscription.currency}/{t('month')}
                  </p>
                  {currentSubscription.next_billing_date && (
                    <p className="text-xs text-green-600 mt-1">
                      {t('nextBilling')}: {new Date(currentSubscription.next_billing_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Basic (Free) */}
          <Card className="border-2 border-gray-200 hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-2xl font-bold mb-2">{t('basic')}</CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-gray-600 ml-2">/ {isYearly ? 'year' : 'month'}</span>
                <div className="text-2xl font-semibold text-gray-500 mt-2">0 SAR</div>
              </div>
              <p className="text-gray-600 mt-4">{t('forIndividuals')}</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('fiftyAICredits')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('threeResearchProjects')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('fiveGBStorage')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('basicRooms')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('communitySupport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('basicDocManagement')}</span>
                </li>
              </ul>
              <Button className="w-full bg-gray-100 text-gray-800 hover:bg-gray-200">
                {t('getStartedFree')}
              </Button>
            </CardContent>
          </Card>

          {/* Premium */}
          <Card className="relative border-cyan-500 shadow-lg">
            <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-cyan-500">
              {t('mostPopular')}
            </Badge>
            <CardHeader className="text-center pb-8 pt-8">
              <CardTitle className="text-2xl font-bold mb-2">{t('premium')}</CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-bold">${isYearly ? '150' : '15'}</span>
                <span className="text-gray-600 ml-2">/ {isYearly ? 'year' : 'month'}</span>
                <div className="text-2xl font-semibold text-gray-500 mt-2">{isYearly ? '562' : '56'} SAR</div>
              </div>
              {isYearly && (
                <p className="text-sm text-green-600 mt-2">Save ${(15 * 12) - 150}/year</p>
              )}
              <CardDescription className="mt-4">{t('forResearchers')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">{t('fiveHundredAICredits')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('tenResearchProjects')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('fiftyGBStorage')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('extendedVirtualRooms')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('prioritySupport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('advancedAI')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('realtimeCollaboration')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('exportFormats')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('documentSharingVersioning')}</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                onClick={() => handleSubscribe(isYearly ? 'premium_yearly' : 'premium_monthly')}
                disabled={processing || (currentSubscription?.plan === 'premium')}
              >
                {processing ? t('processingPayment') : (currentSubscription?.plan === 'premium' ? t('currentPlan') : t('subscribeNow'))}
              </Button>
            </CardFooter>
          </Card>

          {/* Advanced */}
          <Card className="relative border-gray-200">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                {t('advanced')}
                <Sparkles className="h-5 w-5 text-yellow-500" />
              </CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-bold">${isYearly ? '320' : '32'}</span>
                <span className="text-gray-600 ml-2">/ {isYearly ? 'year' : 'month'}</span>
                <div className="text-2xl font-semibold text-gray-500 mt-2">{isYearly ? '1200' : '120'} SAR</div>
              </div>
              {isYearly && (
                <p className="text-sm text-green-600 mt-2">Save ${(32 * 12) - 320}/year</p>
              )}
              <CardDescription className="mt-4">{t('forPowerUsers')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">{t('twoThousandFiveHundredAICredits')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('unlimitedProjectsCollaborators')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('fiveHundredGBStorage')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('unlimitedRoomsRecording')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('support247')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('advancedAnalyticsDashboard')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('apiAccessIntegrations')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('customDataExport')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('enhancedSecurity')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('dedicatedSupportChannel')}</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full bg-gray-900 hover:bg-gray-800"
                onClick={() => handleSubscribe(isYearly ? 'advanced_yearly' : 'advanced_monthly')}
                disabled={processing || (currentSubscription?.plan === 'advanced')}
              >
                {processing ? t('processingPayment') : (currentSubscription?.plan === 'advanced' ? t('currentPlan') : t('subscribeNow'))}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Payment Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CreditCard className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">{t('securePaymentPayPal')}</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ {t('safeSecurePayment')}</li>
                  <li>✓ {t('cancelAnytime')}</li>
                  <li>✓ {t('automaticBilling')}</li>
                  <li>✓ {t('instantAccess')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
